import {Control, Controller, FieldPath, FieldValues, Path, PathValue} from "react-hook-form";
import {Autocomplete, AutocompleteProps, TextField} from "@mui/material";
import {HasId} from "../../types/hasId";

export interface Props<TOption extends HasId, TFieldValues extends FieldValues = FieldValues>
    extends Omit<
        AutocompleteProps<TOption, boolean, boolean, boolean>,
        "renderInput" | "options" | "onChange" | "getOptionLabel"
    > {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    initialValue?: PathValue<TFieldValues, Path<TFieldValues>>;
    options: TOption[];
    onChange?: (id: string | number | undefined | null) => void;
    isLoading?: boolean;
    getOptionLabel: (option: TOption) => string;
    disableClear?: boolean;
    placeholder?: string;
}

export const AutocompleteInput = <TOptions extends HasId, TFieldValues extends FieldValues = FieldValues>(
    props: Props<TOptions, TFieldValues>,
) => {
    const {
        name,
        control,
        options,
        isLoading,
        initialValue,
        getOptionLabel,
        onChange,
        disableClear,
        filterOptions,
        disabled,
        ...autocompleteProps
    } = props;
    return (
        <Controller
            name={name}
            control={control}
            defaultValue={initialValue}
            render={({field: {onChange: onControllerChange, value}, fieldState: {error}}) => {
                const selectedOption = options.find((item) => item.id === value) ?? null;
                return (
                    <Autocomplete
                        id={name}
                        {...autocompleteProps}
                        fullWidth
                        loading={isLoading}
                        options={options}
                        {...(disableClear && {clearIcon: null})}
                        filterOptions={filterOptions}
                        autoComplete={true}
                        autoHighlight={true}
                        disabled={disabled ?? false}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                hiddenLabel
                                margin="none"
                                size="small"
                                type="text"
                                fullWidth
                                placeholder={autocompleteProps.placeholder}
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                }}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                        getOptionLabel={(option: TOptions | string): string => {
                            return getOptionLabel(option as TOptions);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={selectedOption}
                        onChange={(_, newValue) => {
                            onControllerChange(newValue ? (newValue as TOptions).id : null);
                            if (onChange) {
                                onChange(newValue ? (newValue as TOptions).id : null);
                            }
                        }}
                    />
                );
            }}
        />
    );
};
